import { History } from "history";
import { SixdeeCRBTAPIProcessorFactory, SixdeeCRBTAPIProcessorFactoryImpl } from '../core/apiprocessorfactory';
import { ToneDto } from '../core/dto/tonedto';
import { ConfigurationSettings } from './configurationsettings/configurationsettings';
import { ValidateKnownFailures, ValidateKnownFailuresImpl } from './handleknownfailures/validateknownfailures';
import { Logger, LogManager } from "./logger/logger";
import { AudioPlayerImpl } from './player/audioplayerImpl';
import { PlayerManager, PlayerManagerImpl } from './player/playmanager';
import { Property, PropertyImpl } from "./property/property";
import { HandleRedirection, HandleRedirectionImpl } from './redirection/handleredirection';
import { LanguageConst } from "./resources/languageconst";
import { Resources, ResourcesImpl } from "./resources/resources";
import { ThemeConst } from "./resources/themeconst";
import { RouterDataModelProps } from './routerbuilder';
import { ToneCache } from './tonedetails/tonecache';
import { UserContext } from "./usercontext/usercontext";
import { UserDataCache } from './usercontext/userdatacache';

export interface BaseContext
{
    getRouterHistory(): History<any>;
    getProperty(): Property;
    getResources(): Resources;
    getAPIProcessorFactory(): SixdeeCRBTAPIProcessorFactory;
    getRoutingPath(): string;
    getUserContext(): UserContext;
    getPlayerManager(): PlayerManager;
    getConfigurationSettings(): ConfigurationSettings;
    getValidateKnownFailures(): ValidateKnownFailures;
    getRedirectionHandler(): HandleRedirection;
    getToneContext(): ToneDto | null;
    clearToneDetails(): void;
    logout(): void;
    
    setToneContext(toneDto : ToneDto | null): void;
    setUserContext(context: UserContext): void;
    setConfigurationSettings(settingsData: any): void;
}

export class BaseContextImpl implements BaseContext
{
    private logger: Logger = LogManager.getLogger(BaseContextImpl.name);
    private properties: Property;
    private apiProcessorFactory: SixdeeCRBTAPIProcessorFactory;
    private resources: Resources;
    private routingPath: string;
    private userDataCache: UserDataCache;
    private routerHistory: History<any>;
    private playerManager: PlayerManager;
    private validateToken: ValidateKnownFailures;
    private redirectionHandler: HandleRedirection;
    private toneCache: ToneCache;
    
    constructor()
    {
        this.logger.log("BaseContextImpl() invoked");
    }

    init(resourcedLoaded: () => void)
    {
        // this.properties = this.createProperty();
        this.userDataCache = new UserDataCache();
        this.toneCache = new ToneCache();
        this.redirectionHandler = new HandleRedirectionImpl(this);
        this.validateToken = new ValidateKnownFailuresImpl(this);
        this.resources = this.createResources(resourcedLoaded);
        this.apiProcessorFactory = this.createAPIProcessorFactory();
    }

    getRedirectionHandler(): HandleRedirection {
        return this.redirectionHandler;
    }

    setRedirectionHanlder(redirectionHandler: HandleRedirection): void 
    {
        this.redirectionHandler = redirectionHandler;
    }

    getValidateKnownFailures(): ValidateKnownFailures
    {
       return this.validateToken;
    }

    getPlayerManager(): PlayerManager 
    {
       return this.playerManager;
    }

    getProperty(): Property 
    {
      return this.properties;
    }

    getResources(): Resources 
    {
        return this.resources;
    }

    getRouterHistory(): History<any>
    {
        return this.routerHistory;
    }

    getUserContext(): UserContext {
       return this.userDataCache.getUserContext();
    }

    getToneContext(): ToneDto | null {
       return this.toneCache.getToneContext();
    }

    logout(): void {
        console.log("----:" + this.properties.getChannelId());
        
        this.getUserContext().setMsisdn("");
        this.getUserContext().setMsisdnB("");
        this.getUserContext().setPassword("");
        this.getUserContext().setAccessToken("");
        this.getUserContext().setRefreshToken("");
        this.getUserContext().setDeviceId("");
        this.getUserContext().setSecToken("");
        this.getUserContext().setPackName("");
        this.getUserContext().setUserName("");
        this.getUserContext().setChannelId(this.properties.getChannelId());
        this.getUserContext().setPackDisplayName("");
        this.getUserContext().setIsLoggedIn(false);

        this.setUserContext(this.getUserContext());
    }

    clearToneDetails()
    {
        this.toneCache.clearCache();
    }

    setToneContext(toneDto: ToneDto | null)
    {
        this.toneCache.updateToneContext(toneDto);
    }

    getRoutingPath(): string 
    {
       return this.routingPath;
    }

    updateLanguage(count: number, language: LanguageConst, onResourceLoaded: () => void): void 
    {
        this.logger.log("updateLanguage() invoked");

        (this.resources as ResourcesImpl).updateLanguage(count, language, onResourceLoaded);
    }

    updateTheme(theme: ThemeConst, onResourceLoaded: () => void): void 
    {
        this.logger.log("updateTheme() invoked");

        (this.resources as ResourcesImpl).updateTheme(theme, onResourceLoaded);
    }

    getAPIProcessorFactory(): SixdeeCRBTAPIProcessorFactory
    {
        return this.apiProcessorFactory;
    }

    protected createProperty(): Property
    {
        return new PropertyImpl();
    }

    protected createResources(resourcedLoaded: () => void): Resources
    {
        let resources = new ResourcesImpl(this);
        resources.init(this.getUserContext().getLanguage(), ThemeConst.Dark, resourcedLoaded);
        return resources;
    }

    protected createAPIProcessorFactory(): SixdeeCRBTAPIProcessorFactory
    {
        return new SixdeeCRBTAPIProcessorFactoryImpl(this);
    }

    setUserContext(userContext: UserContext)
    {
        this.userDataCache.updateUserContext(userContext);
    }

    getConfigurationSettings(): ConfigurationSettings {
        return this.userDataCache.getConfgiurationSettings();
    }

    setConfigurationSettings(settingsData: any): void 
    {
        this.userDataCache.updateConfigurationSettings(settingsData);
    }

    fromRoutingProps(routingProps: RouterDataModelProps)
    {
        this.setRoutingPath(routingProps.getRoutingPath());
        this.setRouterHistory(routingProps.getRouterHistory());
        this.setProperty(routingProps.getProperty());

        this.playerManager = new PlayerManagerImpl(new AudioPlayerImpl());
    }

    private setRoutingPath(routingPath: string)
    {
        this.routingPath = routingPath;
    }

    private setRouterHistory(routerHistory: History<any>)
    {
        this.routerHistory = routerHistory;
    }

    private setProperty(property: Property)
    {
        this.properties = property;
    }
 }
//package com.sixdee.mytune.util.datacache;

// import android.content.Context;
// import android.content.SharedPreferences;
// import android.content.pm.PackageInfo;

// import com.google.gson.Gson;
// import com.sixdee.androidframework.util.logger.Logger;
// import com.sixdee.mytune.util.mytone.MyToneConst;
// import com.sixdee.mytune.util.mytone.MyToneLanguageConst;

// /**
//  * Created by Vinu on 27-Oct-16.
//  */
// public class DataCache
// {
//     private static final Logger LOGGER = Logger.getLogger(DataCache.class);

//     private static DataCache DATA_CACHE = null;
//     private SharedPreferences sharedPreferences;
//     private Gson gson;
//     private SharedPreferences.Editor editor;
//     private MyToneSessionDto myToneSessionDto;
//     private Context context;
//     //private final MyToneLanguageConst defaultLanguage = MyToneLanguageConst.ZAWGYI;
//     private final MyToneLanguageConst defaultLanguage = MyToneLanguageConst.FRENCH;

//     private DataCache(Context context)
//     {
//         LOGGER.debug("DataCache() invoked");

//         this.sharedPreferences = context.getSharedPreferences("MYTONE", 0);
//         this.gson = new Gson();
//         this.editor = sharedPreferences.edit();
//         this.context = context;

//         try
//         {
//             String json = sharedPreferences.getString(MyToneConst.SHARED_PREFERENCE_KEY, "");

//             if(json == null || json.trim().isEmpty())
//                 myToneSessionDto = new MyToneSessionDto();
//             else
//                 myToneSessionDto = gson.fromJson(json, MyToneSessionDto.class);

//              /* Make english as default language */
//             if(myToneSessionDto.getLanguage() == null)
//                 myToneSessionDto.setLanguage(defaultLanguage);
//         }
//         catch (Exception e)
//         {
//             LOGGER.error("Exception occurred in DataCache()");
//             LOGGER.error(e.fillInStackTrace());

//             myToneSessionDto = new MyToneSessionDto();
//         }
//     }

//     public static DataCache getInstance(Context context)
//     {
//         if(DATA_CACHE == null)
//         {
//             DATA_CACHE = new DataCache(context);
//         }
//         return DATA_CACHE;
//     }

//     public void setMyToneSessionDto(MyToneSessionDto myToneSessionDto)
//     {
//         this.myToneSessionDto = myToneSessionDto;

//         String json = gson.toJson(myToneSessionDto);
//         editor.putString(MyToneConst.SHARED_PREFERENCE_KEY, json);
//         editor.commit();
//     }

//     public MyToneSessionDto getMyToneSessionDto()
//     {
//         return myToneSessionDto;
//     }

//     public void clearCache()
//     {
//         LOGGER.info("clearCache() invoked");

//         /* Need to persist user information even after logout */
//         MyToneSessionDto myToneSessionDto = new MyToneSessionDto();
//         myToneSessionDto.setLoggedIn(false);
//         myToneSessionDto.setConnectId(true);
//         myToneSessionDto.setLanguage(defaultLanguage);

//         setMyToneSessionDto(myToneSessionDto);
//     }

//     @Override
//     protected void finalize() throws Throwable
//     {
//         super.finalize();

//         LOGGER.info("finalize() invoked");
//     }

//     //Programmatically get the current version Name
//     public String getApplicationVersionName() {

//         try {
//             PackageInfo packageInfo = context.getPackageManager().getPackageInfo(context.getPackageName(), 0);
//             return packageInfo.versionName;
//         } catch(Exception ignored){}
//         return "";
//     }
// }




// WEBPACK FOOTER //
// ./src/com/sixdee/crbtportal/utils/basecontext.tsx